/*
 * TableHeader
 *
 * A <thead> component to create a table header (include the sort button).
 */

import { faInfoCircle, faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { HTMLAttributes, ReactElement, ThHTMLAttributes } from "react";

import Icon from "src/components-v2/Icon";
import PopupTooltip from "src/components-v2/PopupTooltip";

export type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement> & {
  /**
   * Table headers
   */
  headers?: Record<
    string,
    ThHTMLAttributes<HTMLTableCellElement> & {
      sort: boolean;
      title: string;
      tooltip: string | ReactElement;
      className?: string;
      required?: boolean;
    }
  >;

  /**
   * Current sorting type (Ascending or Descending)
   * @type {"SORT_ASC" | "SORT_DESC"}
   * @default {"SORT_DESC"}
   */
  sortType?: "SORT_ASC" | "SORT_DESC";

  /**
   * Current sorting key (the id of the column)
   */
  sortBy?: string;

  /**
   * Set blank column in the right (e.g: for actions)
   */
  offset?: number;

  /**
   * Handler when sort button is clicked
   */
  onSort?: (_args: { sort_by: string; sort_type: "SORT_ASC" | "SORT_DESC" }) => void;

  /**
   * custom function to check visibility of head
   */
  columnChecker?: (args?: any) => any;
};

const defaultProps = {
  headers: {},
  sortType: "SORT_DESC",
  sortBy: "",
  offset: 0,
  onSort: () => {},
  columnChecker: () => {}
};

function TableHeader(props: TableHeaderProps) {
  const { className, headers, sortType, sortBy, offset, onSort, columnChecker, ...restProps } = props;

  function handleSort(key: string) {
    const type = sortType === "SORT_DESC" ? "SORT_ASC" : "SORT_DESC";
    onSort({ sort_by: key, sort_type: type });
  }

  return (
    <thead className={classNames("u-whitespace-nowrap", className)} {...restProps}>
      <tr>
        {Object.entries(headers).map(([key, item], i) => {
          return (
            <th
              key={i}
              className={classNames(
                columnChecker(key),
                item.sort && "hover:u-text-brick",
                sortBy === key && "u-text-flip-orange",
                item.className
              )}
              colSpan={item.colSpan}
            >
              {item.sort ? (
                <span className="u-cursor-pointer" onClick={() => handleSort(key)}>
                  <span>{item.title}</span>
                  <Icon
                    className="u-ml-1"
                    icon={sortBy !== key ? faSort : sortType === "SORT_DESC" ? faSortDown : faSortUp}
                  />
                </span>
              ) : (
                item.title
              )}
              {item.required && (
                <span className="u-text-ketchup-tomato u-text-medium" data-testid="qa-field-label-required">
                  *
                </span>
              )}
              {item.tooltip && (
                <PopupTooltip.Hover position="top" className="u-inline-block" content={item.tooltip}>
                  <Icon icon={faInfoCircle} className="u-ml-1" />
                </PopupTooltip.Hover>
              )}
            </th>
          );
        })}
        {offset > 0 && [...Array(offset)].map((_, i) => <th key={i} />)}
      </tr>
    </thead>
  );
}

TableHeader.defaultProps = defaultProps;

export default TableHeader;
